import React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LayoutDashboard, FileText, Users, LogOut, ShieldCheck, User, Inbox, ClipboardList } from 'lucide-react';

const PortalLayout = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  if (!user) return null;

  const doLogout = () => {
    logout();
    nav('/portal/login', { replace: true });
  };

  const isAdmin = user.role === 'admin';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_site-creator-3034/artifacts/pbfdilsv_Finirio.JPG"
              alt="Finirio"
              className="h-9 w-auto object-contain"
            />
            <span className="hidden sm:inline text-sm font-semibold text-slate-500 border-l border-slate-200 pl-3 ml-1">
              Internal Portal
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-sm text-slate-600">
              <div className="w-8 h-8 rounded-full grid place-items-center bg-[#eaf3fb] text-[#1e73be] font-semibold">
                {(user.name || user.email).charAt(0).toUpperCase()}
              </div>
              <div className="leading-tight">
                <p className="font-semibold text-slate-800">{user.name}</p>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">{user.role}</p>
              </div>
            </div>
            <button
              onClick={doLogout}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <aside className="lg:sticky lg:top-20 h-fit">
          <nav className="bg-white border border-slate-200 rounded-lg p-2 space-y-1">
            <SideLink to="/portal" end icon={LayoutDashboard} label="Dashboard" />
            <SideLink to="/portal/profile" icon={User} label="My Profile" />
            {isAdmin && (
              <>
                <div className="mt-3 mb-1 px-3 text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Admin</div>
                <SideLink to="/portal/admin/users" icon={Users} label="Users" />
                <SideLink to="/portal/admin/pages" icon={FileText} label="Pages" />
                <SideLink to="/portal/admin/leads" icon={Inbox} label="Leads & Forms" />
                <SideLink to="/portal/admin/customer-details" icon={ClipboardList} label="Customer Details" />
                <div className="mt-3 mb-1 px-3 text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Recruitment</div>
                <SideLink to="/portal/admin/assessment" end icon={ClipboardList} label="Assessment" />
                <SideLink to="/portal/admin/assessment/candidates" icon={Users} label="Candidates" />
                <SideLink to="/portal/admin/assessment/questions" icon={FileText} label="Questions" />
              </>
            )}
          </nav>
          {isAdmin && (
            <div className="mt-4 rounded-lg border border-[#1e73be]/20 bg-[#eaf3fb] p-3 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-[#1e73be]" />
              <p className="text-[12px] text-slate-700 leading-relaxed">
                You have <b>Admin</b> privileges. Handle role assignments carefully.
              </p>
            </div>
          )}
        </aside>
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const SideLink = ({ to, end, icon: Icon, label }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `flex items-center gap-2 px-3 py-2 rounded-md text-[14px] font-medium transition-colors ${
        isActive
          ? 'bg-[#1e73be] text-white'
          : 'text-slate-700 hover:bg-slate-100'
      }`
    }
  >
    <Icon className="w-4 h-4" /> {label}
  </NavLink>
);

export default PortalLayout;
